import { useCallback, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { FallbackProps } from 'react-error-boundary';
import { AxiosError, isAxiosError } from 'axios';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import { Text } from '@antimatterhq/ui/components/typography';
import Spacer from '@antimatterhq/ui/components/spacer';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import { datadogLogs } from '@datadog/browser-logs';
import { DetectionRulesForm } from '../../_partials/DetectionRulesForm';
import { AddDetectionRuleSchemaValues } from '@/zod/schemas/detectionRules/AddDetectionRule';
import {
  convertAddDetectionRuleValuesToRule,
  getAddDetectionRuleFormInitialValues,
} from '@/lib/detectionRules/detectionRulesHelper';
import { useGetRule } from '@/hooks/core/useGetRule';
import { useUpdateRule } from '@/hooks/core/useUpdateRule';
import { SuspenseErrorBoundary } from '@/components/errorBoundary/SuspenseErrorBoundary';
import { TwoColumnLayout } from '@/components/container';
import { BackLink } from '@/components/link/back/BackLink';
import { DATADOG_MESSAGE, getDatadogDetectionRule } from '@/utils/datadogUtils';
import { useGetRulesInfinite } from '@/hooks/core/useGetRulesInfinite';
import { AsyncValidationProvider } from '@/contexts/AsyncValidationContext';

function DetectionRulesEditPageSuspense() {
  const navigate = useNavigate();
  const match = useMatch('/configure/detection-rules/edit/:name');
  const { name = '' } = match?.params || {};
  const { optimisticUpdate } = useGetRulesInfinite();

  const { mutateAsync: updateRuleAsync } = useUpdateRule();
  const { data: getRuleByNameData } = useGetRule({ name });
  const [responseError, setResponseError] = useState<
    AxiosError<{ message: string }> | undefined
  >(undefined);

  const initialValues = getAddDetectionRuleFormInitialValues(getRuleByNameData);

  const onSubmit = useCallback(
    async (values: AddDetectionRuleSchemaValues) => {
      const rule = convertAddDetectionRuleValuesToRule(values);
      setResponseError(undefined);
      await updateRuleAsync(
        {
          name,
          data: rule,
        },
        {
          onSuccess: async () => {
            datadogLogs.logger.info(
              DATADOG_MESSAGE.DETECTION_RULE.UPDATE.SUCCESS,
              getDatadogDetectionRule(rule),
            );
            optimisticUpdate(rule);
            navigate('/configure/detection-rules');
          },
          onError: async ({ error }) => {
            datadogLogs.logger.error(
              DATADOG_MESSAGE.DETECTION_RULE.UPDATE.FAILURE,
              getDatadogDetectionRule(rule),
              error,
            );
            setResponseError(error as AxiosError<{ message: string }>);
          },
          toasts: {
            success: 'Rule updated successfully',
            error: 'Failed to updated rule',
          },
        },
      );
    },
    [name, navigate, optimisticUpdate, updateRuleAsync],
  );

  return (
    <AsyncValidationProvider>
      <main data-testid="detection-rules-edit-page">
        <Spacer size="lg" />
        <div>
          <BackLink fallbackRoute="/configure/detection-rules" />
        </div>
        <Spacer size="base" />
        <DetectionRulesForm
          isInEditMode={true}
          responseError={responseError}
          initialValues={initialValues}
          onSubmit={onSubmit}
        />
      </main>
    </AsyncValidationProvider>
  );
}

const EditDetectionRulesErrorBoundaryFallback: React.FC<FallbackProps> = ({
  resetErrorBoundary,
  error,
}) => {
  const match = useMatch('/configure/detection-rules/edit/:name');
  const { name = '' } = match?.params || {};
  const isNotFoundError = isAxiosError(error) && error.response?.status === 404;

  if (isNotFoundError)
    return (
      <GlobalError
        title="Detection rule not found"
        description={`Detection rule "${name}" could not be found`}
        onRetry={async () => resetErrorBoundary()}
        learnMoreLink="/configure/detection-rules"
        learnMoreText="Go to detection rules"
        retryText="Retry"
        className="h-[75vh]"
      />
    );

  const errorMessage = isAxiosError(error)
    ? (error.response?.statusText ?? error.message)
    : error.message;

  return (
    <GlobalError
      title="Error loading detection rule"
      description={errorMessage}
      onRetry={async () => resetErrorBoundary()}
      retryText="Retry"
      className="h-[75vh]"
    />
  );
};

function EditDetectionRulesSuspenseFallback() {
  return (
    <>
      <Spacer size="lg" />

      <TwoColumnLayout.Root>
        <TwoColumnLayout.LeftColumn>
          <div className="flex flex-col gap-4">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-8 w-56" />

            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-10 w-full" />

            <Skeleton className="h-6 w-20" />
            <div className="flex gap-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>

            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-10 w-full" />

            <Skeleton className="h-6 w-20" />
            <div className="flex gap-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
            <Skeleton className="h-10 w-20" />

            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-10 w-full" />
          </div>
        </TwoColumnLayout.LeftColumn>
        <TwoColumnLayout.RightColumn>
          <nav className="sticky top-[40px] flex flex-col gap-4">
            <Text size="lg" className="mb-4 font-bold">
              Table of contents
            </Text>
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </nav>
        </TwoColumnLayout.RightColumn>
      </TwoColumnLayout.Root>
    </>
  );
}

const DetectionRulesEditPage = () => {
  return (
    <SuspenseErrorBoundary
      suspenseFallback={<EditDetectionRulesSuspenseFallback />}
      errorBoundaryFallback={EditDetectionRulesErrorBoundaryFallback}
    >
      <DetectionRulesEditPageSuspense />
    </SuspenseErrorBoundary>
  );
};

export default DetectionRulesEditPage;
